import React, {useEffect, useMemo, useRef, useState} from "react";
import Paper from "@mui/material/Paper";
import {Row} from "reactstrap";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import NewBulkPriceChangeViewStepperSection from "./sections/stepper";
import NewBulkPriceChangeViewBottomActionBar from "./sections/bottom-action-bar";
import BulkPriceChangeProductSelectionSection from "./sections/product-selection";
import BulkPriceChangePriceLevelSelectionSection from "./sections/price-level-selection";
import BulkPriceChangeChangePriceSection from "./sections/change-price";
import BulkPriceChangeReviewAndFinalizeSection from "./sections/review-and-finalize";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import {useHistory, useParams} from "react-router-dom";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import {NewBulkPriceChangeServerStatusIds} from "../index";


/**
 * Different steps for creating a new bulk price change
 */
export const NewBulkPriceChangeSteps = {
    productSelection: 'productSelection',
    priceLevelSelection: 'priceLevelSelection',
    changePrice: 'changePrice',
    reviewAndFinalize: 'reviewAndFinalize',
    finalized: 'finalized'
}

/**
 * The types of the autocomplete entries for a new bulk price change.
 */
export const BulkPriceChangeEntryTypes = {
    category: "Category",
    tag: "Tag",
    part: "PartNumber",
}


const NewBulkPriceChangeView = () => {
    const params = useParams();
    const history = useHistory();
    const [state, setState] = useState({});
    const [loading, setLoading] = useState(true);
    const [savingState, setSavingState] = useState(false);
    const actionRef = useRef();
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('new-bulk-price-change-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.bulkPriceChangeNewBulkPrice(params.id));
        configureBehavioralTool((tags) => tags.bulkPriceChangeNewBulkPrice(params.id));
    }, [configureBehavioralTool, configureNotebookTags, params.id]);

    const requestId = useMemo(() => Number(params.id), [params.id])
    const selectedPriceLevelIds = useMemo(() => state.priceLevels?.map(e => e.id) ?? [], [state.priceLevels])
    const excludedPartIds = useMemo(() => state.excludedParts?.map(e => e.partId) ?? [], [state.excludedParts])
    const selectedItems = useMemo(() =>
            state.selectionParams
                ?.map(e => ({
                    id: e.paramId,
                    type: e.type,
                    value: e.title,
                }))
                ?.filter(e => {
                    if (e.type !== BulkPriceChangeEntryTypes.part)
                        return true;
                    return !excludedPartIds.includes(e.id)
                })
            ?? [],
        [state.selectionParams, excludedPartIds])
    const selectedItemIds = useMemo(() => selectedItems.map(e => e.id), [selectedItems])

    /**
     * With each change in the [id] of the url params:
     * - fetches the information of the bulk-price-change-request from the server.
     */
    useEffect(() => {
        getBulkPriceChangeInformation().then();
    }, [requestId])

    /**
     * Fetches the information of the bulk-price-change-request from the server.
     *
     * * if the current step of this entity is finalized, then navigates the user to the report view
     * @return {Promise<void>}
     */
    const getBulkPriceChangeInformation = async () => {
        setLoading(true);
        const response = await Api.getBulkPriceChangeInformation(requestId);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            const step = convertServerStatusToStep(response.data.requestStatusId, true);
            if (step === NewBulkPriceChangeSteps.finalized) {
                // finalized or undefined. we can safely navigate to the report view.
                return history.replace(routeFunctions.products.bulkPriceChange.report(requestId));
            }
            if (!step)
                return history.goBack();
            setState({
                ...response.data,
                step: step,
                selectionParams: response.data.selectionParams
                    .filter(e => e.type !== 'ExcludedPart')
                    .map(e => ({
                        ...e,
                        type: e.type === "Part"
                            ? BulkPriceChangeEntryTypes.part
                            : e.type === 'Category'
                                ? BulkPriceChangeEntryTypes.category
                                : BulkPriceChangeEntryTypes.tag,
                    })),
                excludedParts: response.data.selectionParams
                    .filter(e => e.type === 'ExcludedPart')
                    .map(e => ({
                        partId: e.paramId,
                        partNumber: e.value,
                        categoryName: "",
                    })),
            });
        }
    }

    /**
     * Updates the status of this price-change entity with the given step.
     * @param {string} step
     * @return {Promise<boolean>}
     */
    const updateBulkPriceChangeStatus = async (step) => {
        const forApi = {
            requestId: requestId,
            statusId: convertServerStatusToStep(step, false),
        }
        const response = await Api.updateBulkPriceChangeStatus(forApi);
        if (!isMounted())
            return false;
        return response?.resultFlag ?? false;
    }

    /**
     * Converts the status of the request to the step or vise-versa.
     *
     * @param {number | string} statusOrStep
     * @param {boolean} toStep whether we want the conversion to be status -> step or step -> status.
     * @return {string | number | undefined}
     */
    const convertServerStatusToStep = (statusOrStep, toStep = true) => {
        let map = {
            [NewBulkPriceChangeServerStatusIds.productSelection]: NewBulkPriceChangeSteps.productSelection,
            [NewBulkPriceChangeServerStatusIds.changePrice]: NewBulkPriceChangeSteps.changePrice,
            [NewBulkPriceChangeServerStatusIds.priceLevelSelection]: NewBulkPriceChangeSteps.priceLevelSelection,
            [NewBulkPriceChangeServerStatusIds.reviewAndFinalize]: NewBulkPriceChangeSteps.reviewAndFinalize,
            [NewBulkPriceChangeServerStatusIds.finalized]: NewBulkPriceChangeSteps.finalized,
        }
        if (!toStep) {
            map = Object.fromEntries(Object.entries(map).map(([statusId, step]) => [step, Number(statusId)]));
        }
        return map[statusOrStep];
    }

    /**
     * Changes the step of the bulk-price-change based on the given step.
     *
     * * syncs the state with the server
     * * if the new step is the [finalized] then navigates the user to the report view with autoStart query.
     * @param {string} step
     */
    const setStep = async (step) => {
        setSavingState(true);
        if (actionRef.current?.submit) {
            const actionSuccessful = await actionRef.current?.submit();
            if (!isMounted())
                return
            if (!actionSuccessful)
                return;
        }
        const successful = await updateBulkPriceChangeStatus(step);
        if (!isMounted())
            return
        setSavingState(false);
        if (!successful)
            return;
        if (step === NewBulkPriceChangeSteps.finalized) {
            return history.replace(routeFunctions.products.bulkPriceChange.report(requestId, true));
        }
        setState(prevState => ({
            ...prevState,
            step: step,
        }));
    }

    /**
     * Sets the selected-params excluded-part-ids and total-affected-parts of the bulk-change-price.
     * @param selectedParams
     * @param totalAffectedParts
     */
    const setProductSelectionState = (selectedParams, totalAffectedParts) => {
        setState(prevState => ({
            ...prevState,
            selectionParams: selectedParams
                    ?.filter(e => e.type !== 'ExcludedPart')
                    ?.map(e => ({
                        ...e,
                        type: e.type === "Part"
                            ? BulkPriceChangeEntryTypes.part
                            : e.type === 'Category'
                                ? BulkPriceChangeEntryTypes.category
                                : BulkPriceChangeEntryTypes.tag,
                    }))
                ?? [],
            excludedParts: selectedParams
                    ?.filter(e => e.type === 'ExcludedPart')
                    ?.map(e => ({
                        partId: e.paramId,
                        partNumber: e.value,
                        categoryName: "",
                    }))
                ?? [],
            totalAffectedParts: totalAffectedParts ?? 0,
        }))
    }

    /**
     * Sets the price levels of the bulk-change-price.
     * @param priceLevels
     */
    const setPriceLevelSelectionState = (priceLevels) => {
        setState(prevState => ({
            ...prevState,
            priceLevels: priceLevels ?? [],
            affectedPriceLevels: priceLevels?.length ?? 0,
        }))
    }

    /**
     * Sets the price-change information of the bulk-change-price.
     * @param amount
     * @param isPercentage
     */
    const setPriceChangeState = (amount, isPercentage) => {
        setState(prevState => ({
            ...prevState,
            requestedAmount: amount ?? 0,
            isPercentage: isPercentage,
        }))
    }

    /**
     * Memo version of the content of the page based on the steps
     * @type {ReactNode}
     */
    const content = useMemo(() => {
        switch (state.step) {
            case NewBulkPriceChangeSteps.productSelection:
                return <BulkPriceChangeProductSelectionSection
                    requestId={requestId}
                    excludedPartIds={excludedPartIds}
                    selectedItems={selectedItems}
                    selectedItemIds={selectedItemIds}
                    setProductSelectionState={setProductSelectionState}
                />
            case NewBulkPriceChangeSteps.priceLevelSelection:
                return <BulkPriceChangePriceLevelSelectionSection
                    requestId={requestId}
                    selectedPriceLevelIds={selectedPriceLevelIds ?? []}
                    setPriceLevelSelectionState={setPriceLevelSelectionState}
                    ref={actionRef}
                />
            case NewBulkPriceChangeSteps.changePrice:
                return <BulkPriceChangeChangePriceSection
                    requestId={requestId}
                    affectedPriceLevels={state.affectedPriceLevels}
                    totalAffectedProducts={state.totalAffectedParts}
                    isPercentage={state.isPercentage}
                    requestedAmount={state.requestedAmount}
                    setPriceChangeState={setPriceChangeState}
                />
            case NewBulkPriceChangeSteps.reviewAndFinalize:
                return <BulkPriceChangeReviewAndFinalizeSection
                    isPercentage={state.isPercentage}
                    requestedAmount={state.requestedAmount}
                    requestId={requestId}
                    priceLevels={state.priceLevels}
                    selectedItemIds={selectedItemIds}
                    operatorName={state.operatorName}
                    submitDate={state.submitDateTime}
                />
            default:
                return <></>

        }
    }, [excludedPartIds, requestId, selectedItemIds, selectedItems, selectedPriceLevelIds, state.affectedPriceLevels, state.isPercentage, state.requestedAmount, state.step, state.totalAffectedParts])

    return (
        <>
            <div
                className={'new-bulk-price-change-view'}
                // ref={mainSectionRef}
            >
                {
                    loading &&
                    <div className={'loading new-bulk-price-change'}>
                        <div/>
                    </div>
                }
                {
                    !loading &&
                    <>
                        <Paper
                            className={'paper d-flex flex-column justify-content-center align-items-center'}
                            style={{borderRadius: '5px 5px 0 0'}}
                        >
                            <Row className='no-gutters w-100 d-flex justify-content-between align-items-start my-4 mt-3'>
                                <h2 className={'text-lg font-weight-semi-bold m-0 ml-3'}>
                                    New Bulk Price Change
                                </h2>
                            </Row>
                            <NewBulkPriceChangeViewStepperSection
                                state={state}
                                setStep={setStep}
                                updatingStep={savingState}
                            />
                        </Paper>
                        {content}
                        <NewBulkPriceChangeViewBottomActionBar
                            state={state}
                            setStep={setStep}
                            updatingStep={savingState}
                        />
                    </>
                }
            </div>
        </>
    );
}

export default NewBulkPriceChangeView;
